import * as React from "react";
import { Button, cn } from "@finster/ui";
import { useAtom } from "jotai";
import { ThumbsDown, ThumbsUp } from "lucide-react";

import {
  trackFeedbackDetailsSubmitted,
  trackFeedbackRatingUpdated,
} from "../../analytics/product";
import { outputFeedbackKey, outputFeedbackRatingsAtom } from "./atoms";
import {
  FEEDBACK_FAILURE_MESSAGE,
  THANK_YOU_DURATION_MS,
  THANK_YOU_MESSAGE,
} from "./constants";
import {
  isFeedbackErr,
  removeOutputFeedback,
  submitOutputFeedback,
} from "./feedbackClient";
import { OutputFeedbackDialog } from "./OutputFeedbackDialog";
import { feedbackAnalyticsProperties, type OutputFeedbackTarget, type OutputFeedbackValue } from "./types";

type FeedbackRatingAction = "submitted" | "changed" | "removed";

interface FeedbackDraft {
  value: OutputFeedbackValue;
  reasons: string[];
}

interface OutputFeedbackProps {
  target: OutputFeedbackTarget;
  /** Amplitude `area`, e.g. `PowerPoint` or `Excel`. */
  area: string;
  className?: string;
}

/**
 * Thumbs up/down on a generated output. One click stores the rating through
 * `/api/v4/feedback`, tracks it in Amplitude, and opens optional reason pills.
 * Clicking the same thumb again removes the rating; clicking the other thumb
 * flips it.
 */
export const OutputFeedback: React.FC<OutputFeedbackProps> = ({
  target,
  area,
  className,
}) => {
  const feedbackKey = outputFeedbackKey(target);
  const [ratings, setRatings] = useAtom(outputFeedbackRatingsAtom);
  const rating = ratings[feedbackKey] ?? null;
  const [draft, setDraft] = React.useState<FeedbackDraft | null>(null);
  const [confirmedAt, setConfirmedAt] = React.useState<number | null>(null);
  const [pending, setPending] = React.useState(false);
  const [detailsPending, setDetailsPending] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const writeInFlightRef = React.useRef(false);
  const detailsPendingRef = React.useRef(false);

  const startWrite = () => {
    if (writeInFlightRef.current) return false;
    writeInFlightRef.current = true;
    setPending(true);
    return true;
  };

  const finishWrite = () => {
    writeInFlightRef.current = false;
    detailsPendingRef.current = false;
    setPending(false);
    setDetailsPending(false);
  };

  React.useEffect(() => {
    if (confirmedAt === null) return undefined;
    const timer = window.setTimeout(() => setConfirmedAt(null), THANK_YOU_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [confirmedAt]);

  const setRating = (next: typeof rating) => {
    setRatings((current) => {
      if (!next) {
        const { [feedbackKey]: _removed, ...rest } = current;
        return rest;
      }
      return { ...current, [feedbackKey]: next };
    });
  };

  const trackRating = (action: FeedbackRatingAction, value: OutputFeedbackValue) => {
    trackFeedbackRatingUpdated({
      action,
      area,
      value,
      ...feedbackAnalyticsProperties(target),
    });
  };

  const handleThumbClick = async (value: OutputFeedbackValue) => {
    if (!startWrite()) return;

    const previous = rating;
    setConfirmedAt(null);
    setErrorMessage(null);

    try {
      if (previous?.value === value) {
        setRating(null);
        setDraft(null);

        const result = await removeOutputFeedback(target);
        if (isFeedbackErr(result)) {
          setRating(previous);
          setConfirmedAt(null);
          setErrorMessage(result.error.message);
        } else {
          trackRating("removed", value);
        }
        return;
      }

      setRating({
        outputId: target.outputId,
        outputType: target.outputType,
        value,
        reasons: [],
      });
      setDraft({ value, reasons: [] });

      const result = await submitOutputFeedback({ target, value, reasons: [] });
      if (isFeedbackErr(result)) {
        setRating(previous);
        setDraft(null);
        setConfirmedAt(null);
        setErrorMessage(result.error.message);
      } else {
        setRating(result.data);
        trackRating(previous ? "changed" : "submitted", value);
      }
    } catch {
      setRating(previous);
      setDraft(null);
      setConfirmedAt(null);
      setErrorMessage(FEEDBACK_FAILURE_MESSAGE);
    } finally {
      finishWrite();
    }
  };

  const handleReasonToggle = (reasonId: string) => {
    if (detailsPendingRef.current) return;
    setDraft((current) => {
      if (!current) return current;
      const reasons = current.reasons.includes(reasonId)
        ? current.reasons.filter((id) => id !== reasonId)
        : [...current.reasons, reasonId];
      return { ...current, reasons };
    });
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (open) return;
    if (detailsPendingRef.current) return;
    setDraft(null);
    if (rating && !pending) setConfirmedAt(Date.now());
  };

  const handleDetailsSubmit = async () => {
    if (!draft) return;
    if (!startWrite()) return;

    const previous = rating;
    setConfirmedAt(null);
    setErrorMessage(null);
    detailsPendingRef.current = true;
    setDetailsPending(true);
    setRating({
      outputId: target.outputId,
      outputType: target.outputType,
      value: draft.value,
      reasons: draft.reasons,
    });

    try {
      const result = await submitOutputFeedback({
        target,
        value: draft.value,
        reasons: draft.reasons,
      });

      if (isFeedbackErr(result)) {
        setRating(previous);
        setConfirmedAt(null);
        setErrorMessage(result.error.message);
        return;
      }

      setRating(result.data);
      trackFeedbackDetailsSubmitted({
        area,
        value: draft.value,
        reasons: draft.reasons,
        ...feedbackAnalyticsProperties(target),
      });
      setDraft(null);
      setConfirmedAt(Date.now());
    } catch {
      setRating(previous);
      setConfirmedAt(null);
      setErrorMessage(FEEDBACK_FAILURE_MESSAGE);
    } finally {
      finishWrite();
    }
  };

  const renderThumb = (value: OutputFeedbackValue) => {
    const isActive = rating?.value === value;
    const Icon = value === "positive" ? ThumbsUp : ThumbsDown;
    const label = value === "positive" ? "Good response" : "Bad response";

    return (
      <Button
        type="button"
        variant="outline"
        size="icon-sm"
        active={isActive}
        onClick={() => void handleThumbClick(value)}
        disabled={pending}
        aria-pressed={isActive}
        aria-label={isActive ? "Remove feedback" : label}
        title={isActive ? "Remove feedback" : label}
        className={cn(
          "amplitude-output-feedback cursor-pointer! rounded-none border-0 hover:bg-outline-hover disabled:cursor-not-allowed! [&_svg]:pointer-events-none",
          !isActive && "text-muted-foreground hover:text-foreground"
        )}
        data-amplitude-element={`output-feedback-${value}`}
        data-output-id={target.outputId}
        data-output-type={target.outputType}
      >
        <Icon className="h-4 w-4" aria-hidden />
      </Button>
    );
  };

  return (
    <>
      <div className={cn("flex flex-col gap-1", className)}>
        <div className="flex items-center gap-2">
          {confirmedAt !== null && (
            <p
              role="status"
              aria-live="polite"
              className="m-0 text-[12px] text-muted-foreground"
            >
              {THANK_YOU_MESSAGE}
            </p>
          )}
          <div className="inline-flex items-center divide-x divide-border overflow-hidden rounded-lg border border-border">
            {renderThumb("positive")}
            {renderThumb("negative")}
          </div>
        </div>
        {errorMessage && !draft && (
          <p role="alert" className="m-0 text-[12px] text-destructive">
            {errorMessage}
          </p>
        )}
      </div>

      {draft && (
        <OutputFeedbackDialog
          open
          value={draft.value}
          reasons={draft.reasons}
          pending={pending}
          dismissible={!detailsPending}
          errorMessage={errorMessage}
          onOpenChange={handleDialogOpenChange}
          onToggleReason={handleReasonToggle}
          onSubmit={() => void handleDetailsSubmit()}
        />
      )}
    </>
  );
};
